import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllBusinessesAdmin } from '../../../services/admin.service';
import {
  Box,
  Chip,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search,
  Business as BusinessIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  SearchRounded as SearchRoundedIcon,
} from '@mui/icons-material';
import Loader from '../../../common/loader';
import {Business} from '../../../validation/admin.validation';
import BusinessCardView from './BusinessCardView';
import BusinessTableView from './BusinessTableView';

const AdminBusiness: React.FC = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchBusinesses = async (params: any) => {
    setLoading(true);
    try {
      const response = await getAllBusinessesAdmin(params.page, params.limit, params.search);
      if (response) {
        setBusinesses(response.data);
        setPaginationData({ page: response.page, limit: response.limit, total: response.total });
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = { page: paginationData.page, limit: paginationData.limit, search };
    fetchBusinesses(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData.page]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      const params = { page: 1, limit: paginationData.limit, search };
      fetchBusinesses(params);
      setPaginationData((prev) => ({ ...prev, page: 1 }));
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleViewDetail = (id: string) => {
    navigate(`/admin/business-details/${id}`);
  };

  const handlePageChange = (_: any, value: number) => {
    setPaginationData((prev) => ({ ...prev, page: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Search and Stats */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 mb-4 lg:mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by name, EIN, or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Chip
                icon={<BusinessIcon />}
                label={`Total Businesses: ${paginationData.total}`}
                variant="filled"
                color="primary"
                size="medium"
                sx={{
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '& .MuiChip-icon': {
                    color: 'white',
                  },
                }}
              />
              <div className="flex items-center gap-2 rounded-xl border border-gray-300 overflow-hidden">
                <Tooltip title="Card view">
                  <IconButton
                    onClick={() => setViewMode('card')}
                    className={`!p-2 ${viewMode === 'card' ? 'bg-gray-100' : ''}`}
                    size="small"
                  >
                    <ViewModuleIcon
                      className={viewMode === 'card' ? 'text-black' : 'text-gray-500'}
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Table view">
                  <IconButton
                    onClick={() => setViewMode('table')}
                    className={`!p-2 ${viewMode === 'table' ? 'bg-gray-100' : ''}`}
                    size="small"
                  >
                    <ViewListIcon
                      className={viewMode === 'table' ? 'text-black' : 'text-gray-500'}
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : businesses.length === 0 ? (
          <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-center mb-4">
              <div className="animate-pulse">
                <SearchRoundedIcon className="text-gray-400" sx={{ fontSize: 48 }} />
              </div>
            </div>
            <Typography variant="h6" className="mt-4 text-gray-700">
              No Businesses Found
            </Typography>
            <Typography variant="body2" className="text-gray-500">
              Try adjusting your search criteria.
            </Typography>
          </Box>
        ) : (
          <>
            {viewMode === 'card' ? (
              <BusinessCardView businesses={businesses} onViewDetail={handleViewDetail} />
            ) : (
              <BusinessTableView businesses={businesses} onViewDetail={handleViewDetail} />
            )}

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
              <Pagination
                count={Math.ceil(paginationData.total / paginationData.limit)}
                page={paginationData.page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size="medium"
                className="self-center sm:self-auto"
              />
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-right">
                Showing {(paginationData.page - 1) * paginationData.limit + 1}{' '}
                to{' '}
                {Math.min(
                  paginationData.page * paginationData.limit,
                  paginationData.total
                )}{' '}
                of {paginationData.total} results
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminBusiness;
